import {
    Dashboard as DashboardIcon,
    People as PeopleIcon,
    Assignment as AssignmentIcon,
    LocationOn as LocationIcon,
    Receipt as ReceiptIcon,
    Edit as EditIcon,
    Phonelink as PhonelinkIcon,
    Lock as LockIcon,
    Map as MapIcon,
    MyLocation as LiveTrackingIcon,
    Rule as RuleIcon,
    AccountTree as HierarchyIcon,
    Notifications as NotificationsIcon,
    Settings as SettingsIcon,
    Assessment as ActivityReportIcon,
    Business as BusinessIcon,
    Security as SecurityIcon,
    ManageAccounts as ManageAccountsIcon,
    PersonAdd as PersonAddIcon,
    Straighten as StraightenIcon,
    AdminPanelSettings as AdminPanelSettingsIcon,
    DragIndicator as DragIndicatorIcon,
    Payments as PaymentsIcon,
    Route as RouteIcon,
    WrongLocation as LocationExceptionIcon,
    ContentCopy as DuplicateIcon,
    History as UploadHistoryIcon,
    Inventory2 as UploadArchiveIcon,
    AccountTreeOutlined as AccountTreeOutlinedIcon,
    SwapHoriz as SwapHorizIcon,
    Timer as TimerIcon,
} from '@mui/icons-material';

// ─── Menu definition ─────────────────────────────────────────────────────────
//
// ONE grouped tree for every role (UI plan, Sidebar). Each leaf says which
// roles see it; feature keys still gate it per user on top of that. A leaf
// without `roles` is an admin-console page (ADMIN and SUPER_ADMIN, incl.
// custom roles built on ADMIN such as Area Manager / Team Leader). `roles: SA`
// marks pages only Super Admin has today, so regrouping changed nobody's
// access. Groups with no visible children are dropped by the filter.

const SA  = ['SUPER_ADMIN'];
const EMP = ['EMPLOYEE'];
const ADMIN_ROLES = ['SUPER_ADMIN', 'ADMIN'];

const MENU = [
    { text: 'Dashboard', icon: DashboardIcon, path: '/admin/dashboard',    feature: 'ADMIN_DASHBOARD' },
    { text: 'Dashboard', icon: DashboardIcon, path: '/employee/dashboard', roles: EMP },
    {
        type: 'group', text: 'Tracking', icon: LiveTrackingIcon,
        children: [
            { text: 'Employee Tracking',   icon: PeopleIcon,              path: '/admin/employee-tracking',     feature: 'EMPLOYEE_TRACKING' },
            { text: 'Tracking & Maps',     icon: LiveTrackingIcon,        path: '/admin/tracking',              feature: 'TRACKING_MAPS' },
            { text: 'Punch Tracking Alerts', icon: NotificationsIcon,    path: '/admin/punch-tracking-alerts', feature: 'PUNCH_TRACKING_ALERTS', roles: SA },
            { text: 'Punch Review Center', icon: LocationExceptionIcon,  path: '/admin/punch-review',          badgeKey: 'PUNCH_REVIEW' },
            { text: 'Distance Verify',     icon: StraightenIcon,         path: '/admin/distance-verification', feature: 'DISTANCE_VERIFICATION', roles: SA },
        ],
    },
    {
        type: 'group', text: 'Punch', icon: ReceiptIcon,
        children: [
            { text: 'Punch History',     icon: ReceiptIcon,    path: '/employee/punch-history',     roles: EMP },
            { text: 'Punch Corrections', icon: EditIcon,       path: '/employee/punch-corrections', roles: EMP },
            { text: 'Daily Summary',     icon: AssignmentIcon, path: '/employee/daily-summary',     roles: EMP },
        ],
    },
    {
        type: 'group', text: 'Allowance', icon: LocationIcon,
        children: [
            { text: 'Create Allowance',  icon: AssignmentIcon, path: '/employee/create-allowance',  roles: EMP },
            { text: 'Allowance History', icon: LocationIcon,   path: '/employee/allowance-history', roles: EMP },
        ],
    },
    {
        type: 'group', text: 'Organization', icon: BusinessIcon,
        children: [
            { text: 'Geography / Structure', icon: MapIcon,               path: '/admin/organization-settings',   feature: 'ORGANIZATION_SETTINGS', roles: SA },
            { text: 'Customer & Loan Data',  icon: PeopleIcon,            path: '/admin/customer-loan-data',      feature: 'ORGANIZATION_SETTINGS', roles: SA },
            { text: 'Employee Data',         icon: ManageAccountsIcon,    path: '/admin/employee-data',           feature: 'ORGANIZATION_SETTINGS', roles: SA },
            { text: 'Device Management',     icon: PhonelinkIcon,         path: '/admin/device-management',       feature: 'DEVICE_MANAGEMENT', badgeKey: 'DEVICE_MANAGEMENT', roles: SA },
            { text: 'Notification Control', icon: NotificationsIcon,     path: '/admin/notification-control',    feature: 'NOTIFICATION_CONTROL', roles: SA },
            { text: 'Correction Settings',  icon: RuleIcon,               path: '/admin/correction-settings',     feature: 'CORRECTION_SETTINGS', roles: SA },
            { text: 'Feature Assignment',   icon: DragIndicatorIcon,      path: '/admin/feature-assignment',      feature: 'FEATURE_ASSIGNMENT', roles: SA },
            { text: 'Feature Access Control', icon: SecurityIcon,         path: '/admin/feature-access-control',  feature: 'FEATURE_ACCESS_CONTROL', roles: SA },
        ],
    },
    {
        type: 'group', text: 'Employee', icon: PeopleIcon,
        children: [
            { text: 'Punch Details', icon: AssignmentIcon, path: '/admin/punch-details', feature: 'PUNCH_DETAILS' },
            {
                type: 'group', text: 'Employee', icon: PeopleIcon,
                children: [
                    { text: 'Create Employee',    icon: PersonAddIcon,           path: '/admin/create-user',        feature: 'CREATE_USER', roles: SA },
                    { text: 'Employees List',     icon: ManageAccountsIcon,      path: '/admin/users',              feature: 'USER_MANAGEMENT', roles: SA },
                    { text: 'Employee Hierarchy', icon: AccountTreeOutlinedIcon, path: '/admin/employee-hierarchy', feature: 'EMPLOYEE_HIERARCHY', roles: SA },
                ],
            },
            {
                type: 'group', text: 'Role & Access', icon: SecurityIcon,
                children: [
                    { text: 'Manage Roles',       icon: AdminPanelSettingsIcon,  path: '/admin/role-management',     feature: 'ROLE_MANAGEMENT', roles: SA },
                    { text: 'Legal & Privacy',    icon: SecurityIcon,            path: '/admin/legal', roles: SA },
                    { text: 'Designations',        icon: BusinessIcon,            path: '/admin/designations',        feature: 'MASTER_DATA', roles: SA },
                    { text: 'Feature Management', icon: SettingsIcon,            path: '/admin/feature-management',  feature: 'FEATURE_MANAGEMENT', roles: SA },
                    { text: 'Password Management', icon: LockIcon,              path: '/admin/password-management', feature: 'PASSWORD_MANAGEMENT', roles: SA },
                ],
            },
            // The remaining groups from the enterprise spec — Organization
            // Assignment (Branch/Region/Area/Designation Mapping), Administration
            // (Reset Password / Training), plus the Employee → Employee Transfer
            // and Role & Access → User Approval leaves — are intentionally NOT
            // rendered yet: their pages don't exist, and the sidebar must never
            // show dead links or empty, unclickable groups. The recursive
            // renderer below already supports unlimited nesting, so each one
            // slots in here as a config-only addition whenever its module ships.
            { text: 'Case Transfer', icon: SwapHorizIcon,      path: '/admin/case-transfer', feature: 'CASE_TRANSFER', roles: SA },
            { text: 'Team Activity', icon: ActivityReportIcon, path: '/admin/team-activity', feature: 'TEAM_ACTIVITY', roles: SA },
        ],
    },
    {
        type: 'group', text: 'Collection', icon: PaymentsIcon,
        children: [
            { text: 'My Collections',         icon: PaymentsIcon,       path: '/employee/my-collections',      roles: EMP },
            { text: 'Customer Map',           icon: MapIcon,            path: '/employee/customer-map',        roles: EMP },
            { text: 'Collection Plan Report', icon: ActivityReportIcon, path: '/admin/collection-plan-report', feature: 'COLLECTION_PLAN_REPORT' },
            { text: 'Collection History',     icon: AssignmentIcon,     path: '/admin/collection-history',     feature: 'COLLECTION_HISTORY' },
            { text: 'Collection & Activity',  icon: ActivityReportIcon, path: '/admin/collection-activity',    feature: 'COLLECTION_ACTIVITY_REPORT' },
            { text: 'Visit Planner',          icon: RouteIcon,          path: '/admin/visit-planner',          feature: 'VISIT_PLANNER' },
            { text: 'Customer Map',           icon: MapIcon,            path: '/admin/customer-map',           feature: 'CUSTOMER_MAP' },
            { text: 'CRM Visits',             icon: LocationIcon,       path: '/admin/crm-visits',             feature: 'CRM_VISITS' },
        ],
    },
    {
        type: 'group', text: 'Approvals', icon: AssignmentIcon,
        children: [
            { text: 'Pending Approvals',   icon: AssignmentIcon, path: '/admin/pending-approvals',     feature: 'PENDING_APPROVALS', badgeKey: 'PENDING_APPROVALS' },
            // Reuses PENDING_APPROVALS' feature key so anyone who can already see
            // Pending Approvals sees this too, no separate Feature Assignment step required.
            { text: 'Collection Corrections', icon: EditIcon,    path: '/admin/collection-corrections', feature: 'PENDING_APPROVALS', badgeKey: 'COLLECTION_CORRECTIONS' },
            { text: 'Profile Approvals',    icon: AssignmentIcon, path: '/admin/profile-approval',      feature: 'PROFILE_APPROVALS', badgeKey: 'PROFILE_APPROVALS' },
            { text: 'Hierarchy Management', icon: HierarchyIcon,          path: '/admin/hierarchy/management',    feature: 'HIERARCHY_MANAGEMENT', roles: SA },
            { text: 'Approval Configuration', icon: SettingsIcon,         path: '/admin/hierarchy/configuration', feature: 'APPROVAL_CONFIGURATION', roles: SA },
            { text: 'Approval Matrix',      icon: RuleIcon,                path: '/admin/hierarchy/matrix',        feature: 'APPROVAL_MATRIX', roles: SA },
            { text: 'Approval Levels',      icon: DragIndicatorIcon,       path: '/admin/hierarchy/levels',        feature: 'APPROVAL_LEVELS', roles: SA },
            { text: 'Hierarchy Tree',       icon: AccountTreeOutlinedIcon, path: '/admin/hierarchy/tree',          feature: 'HIERARCHY_TREE', roles: SA },
            { text: 'Approval History',     icon: AssignmentIcon,          path: '/admin/hierarchy/history',       feature: 'APPROVAL_HISTORY', roles: SA },
            { text: 'Delegation',           icon: SwapHorizIcon,           path: '/admin/hierarchy/delegation',    feature: 'DELEGATION', roles: SA },
            { text: 'Escalation Rules',     icon: TimerIcon,                path: '/admin/hierarchy/escalation',    feature: 'ESCALATION_RULES', roles: SA },
        ],
    },
    {
        type: 'group', text: 'Collection Upload', icon: PaymentsIcon,
        children: [
            { text: 'Collection Upload',   icon: PaymentsIcon,      path: '/admin/collection-upload',     feature: 'COLLECTION_UPLOAD' },
            { text: 'Duplicate Collection Records', icon: DuplicateIcon, path: '/admin/duplicate-collections', feature: 'COLLECTION_DUPLICATE_LOG', roles: SA },
            { text: 'Upload History',      icon: UploadHistoryIcon, path: '/admin/upload-history',        feature: 'UPLOAD_HISTORY', roles: SA },
            { text: 'Upload Archive',      icon: UploadArchiveIcon, path: '/admin/upload-archive',        feature: 'UPLOAD_ARCHIVE', roles: SA },
        ],
    },
];

/** The menu one user sees: role first, then (for admin roles) the feature
 * toggles. Same visibility rules as the three old lists. Exported for tests. */
export const buildMenu = ({ effectiveRole, isCustomRole, enabledFeatures = {} }) => {
    const role = ADMIN_ROLES.includes(effectiveRole) ? effectiveRole : 'EMPLOYEE';
    const featureOk = (item) => {
        // Employee pages carry no feature keys; a built-in Super Admin sees
        // everything; ADMIN hides only what is explicitly off; custom roles
        // see only what has been switched on for them.
        if (!item.feature || role === 'EMPLOYEE') return true;
        if (isCustomRole) return enabledFeatures[item.feature] === true;
        if (role === 'SUPER_ADMIN') return true;
        return enabledFeatures[item.feature] !== false;
    };
    const filter = (items) => items.reduce((acc, item) => {
        if (item.type === 'group') {
            const children = filter(item.children);
            if (children.length > 0) acc.push({ ...item, children });
        } else if ((item.roles || ADMIN_ROLES).includes(role) && featureOk(item)) {
            acc.push(item);
        }
        return acc;
    }, []);
    return filter(MENU);
};
